import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { IoChevronDown } from 'react-icons/io5';
import { TbExternalLink } from 'react-icons/tb';

import {
    Box,
    Button,
    Flex,
    Group,
    HoverCard,
    Indicator,
    MantineBreakpoint,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
    UnstyledButton,
} from '@mantine/core';

import { AuthenticationHelpers } from 'helpers/AuthenticationHelpers';

import { UpdateMessage } from 'components/header/components/UpdateMessage';
import { NavItem, useNavItems } from 'hooks/use-nav-items';

import cx from './Menu.module.scss';

const MOBILE_BREAKPOINT: MantineBreakpoint = 'sm';

export const Menu = ({ dynamic }: { dynamic?: boolean }) => {
    const navItems = useNavItems();

    return (
        <Group h="100%" gap={4} wrap="nowrap" className={cx.root}>
            {navItems.map((item) => (
                <HoverCard position="bottom-start" offset={0} closeDelay={0} key={item.title}>
                    <HoverCard.Target>
                        <Box pos="relative">
                            <Box className={cx.hitarea} />
                            <MenuLink item={item} />
                        </Box>
                    </HoverCard.Target>

                    {!!item.items?.length && (
                        <HoverCard.Dropdown
                            className={cx.dropdown}
                            visibleFrom={MOBILE_BREAKPOINT}
                            data-navigation-dropdown="true"
                        >
                            <Stack key={item.title} gap="lg">
                                {item.items.map((item) => (
                                    <MenuDropdownLink key={item.title} item={item} />
                                ))}
                                {dynamic && item.showUpdateMessage && <UpdateMessage />}
                            </Stack>
                            {item.bottomSection && <BottomSection bottomSection={item.bottomSection} />}
                        </HoverCard.Dropdown>
                    )}
                </HoverCard>
            ))}
        </Group>
    );
};

// On the login page the menu highlights the destination the user is heading to, not the login page itself.
const useHighlightPath = () => {
    const pathName = usePathname();

    return AuthenticationHelpers.loginRedirectPath(pathName) ?? pathName;
};

const MenuLink = ({ item }: { item: NavItem }) => {
    const pathName = useHighlightPath();

    const allUrls = (item.items || [])
        .map((item) => [
            item.href,
            item.categories?.map((category) => [
                category.href,
                category.subcategories?.map((subcategory) => subcategory.href),
            ]),
        ])
        .flat(Infinity)
        .filter(Boolean) as string[];

    const active =
        pathName && (allUrls.includes(pathName) || item.highlightPatterns?.some((pattern) => pattern.test(pathName)));
    const dropdown = !!item.items?.length;

    return (
        <UnstyledButton
            className={cx.linkWrapper}
            component={item.href ? Link : undefined}
            href={item.href ?? '#'}
            visibleFrom={MOBILE_BREAKPOINT}
            data-active={active}
            data-navigation-dropdown={dropdown}
        >
            <Box className={cx.link}>
                {item.title}
                {dropdown && <IoChevronDown size={12} />}
            </Box>
        </UnstyledButton>
    );
};

type NavItemItem = NonNullable<NavItem['items']>[number];

export const MenuDropdownLink = ({ item }: { item: NavItemItem }) => {
    return (
        <>
            <UnstyledButton
                className={cx.dropdownLink}
                component={Link}
                href={item.href}
                target={item.external ? '_blank' : undefined}
            >
                <Group wrap="nowrap" align="center">
                    {item.icon && (
                        <Indicator size={8} offset={2} color="red" disabled={!item.highlighted}>
                            <ThemeIcon size={34} variant={item.highlighted ? 'filled' : 'default'} radius="md">
                                {item.icon}
                            </ThemeIcon>
                        </Indicator>
                    )}
                    <div>
                        <Flex align="center" gap={4}>
                            <Text fw={600}>{item.title}</Text>
                            {item.external && <TbExternalLink size={12} strokeWidth={1.5} style={{ marginLeft: 6 }} />}
                        </Flex>

                        {item.description && (
                            <Text size="sm" c="dimmed" maw={500}>
                                {item.description}
                            </Text>
                        )}
                    </div>
                </Group>
            </UnstyledButton>

            {item.children}

            {item.categories && <MenuCategoriesGrid categories={item.categories} />}
        </>
    );
};

export const MenuCategoriesGrid = ({ categories }: { categories: NavItemItem['categories'] }) => {
    const isExternalItem = (item: { external?: boolean; href?: string }) =>
        item.external || item.href?.startsWith('http');

    return (
        <SimpleGrid cols={{ base: 1, mobileNav: 2, lg: 3 }} spacing="lg">
            {categories?.map((item) => (
                <Stack key={item.key ?? item.title?.toString()} gap="sm" miw={200}>
                    {item.href ? (
                        <UnstyledButton className={cx.dropdownCategory} component={Link} href={item.href}>
                            {item.title}
                        </UnstyledButton>
                    ) : (
                        <Text className={cx.dropdownCategory}>{item.title}</Text>
                    )}

                    <Stack gap="0">
                        {item.subcategories?.map((item) => (
                            <UnstyledButton
                                key={item.title}
                                className={cx.dropdownSubcategory}
                                component={Link}
                                href={item.href}
                                target={isExternalItem(item) ? '_blank' : undefined}
                                data-highlighted={item.highlighted || undefined}
                            >
                                {item.icon}
                                {item.title}
                                {isExternalItem(item) && (
                                    <TbExternalLink
                                        size={12}
                                        strokeWidth={1.5}
                                        style={{ marginLeft: 6, marginBottom: -1 }}
                                    />
                                )}
                            </UnstyledButton>
                        ))}
                    </Stack>
                </Stack>
            ))}
        </SimpleGrid>
    );
};

const BottomSection = ({ bottomSection }: { bottomSection: NonNullable<NavItem['bottomSection']> }) => {
    return (
        <Group justify="space-between" fz="sm" className={cx.dropdownFooter}>
            <div>
                <Text fz="md" fw={600}>
                    {bottomSection.title}
                </Text>
                <Text inherit c="dimmed">
                    {bottomSection.description}
                </Text>
            </div>
            <Button variant="filled" component={Link} {...bottomSection.button}>
                {bottomSection.button.label}
            </Button>
        </Group>
    );
};
